/**
 * F 128 Cut 1 — markdown output allowlist (d000697 §2, layer 3).
 *
 * markdown-it already gives us two layers of safety on the SOURCE:
 *   1. `html: false` — raw HTML in the document is escaped, never passed through
 *   2. `validateLink` — `javascript:` / `data:` hrefs are rejected
 *
 * This is the third, and it runs over our OWN output rather than the source.
 * It exists because the FCr plugin injects markup the source never contained
 * (cross-ref chips, icon placeholders). A plugin bug — or a future one that
 * interpolates a value it should not — must not be able to emit an `<img
 * onerror>` into a document we then hand to a consumer.
 *
 * It runs on a STRING and needs no DOM, so it does not break SSR or Node
 * consumers. DOMPurify would cost ~20 KB gz and require a real DOM for a
 * check that a tag scan already does.
 *
 * Design note: this strips DISALLOWED tags rather than trying to repair
 * malformed HTML. A streaming consumer can call `sanitizeMarkdownOutput`
 * per delta; stripping a partially-emitted tag is cheaper and safer than
 * trying to close it.
 */

/** Tags we are willing to emit. Per d000697 §2. */
const ALLOWED_TAGS = new Set([
  'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li',
  'blockquote', 'pre', 'code',
  'a', 'strong', 'em', 's', 'del',
  'table', 'thead', 'tbody', 'tr', 'th', 'td',
  'img', 'hr', 'br', 'span', 'sup', 'sub',
  // F 128: the :icon[name]: plugin emits a placeholder span, and d000697 §2
  // calls out <svg> explicitly as a tag our own output may contain. Cut 1
  // renders an inert placeholder, never an interpolated SVG, but the tag is
  // permitted so a Cut 3 icon sprite is not stripped the moment it lands.
  'svg',
]);

/** Attributes permitted per tag. `*` applies to every allowed tag. */
const GLOBAL_ATTRS = new Set(['class', 'id', 'title', 'dir', 'lang']);
const TAG_ATTRS: Record<string, Set<string>> = {
  a: new Set(['href', 'data-fc-ref', 'data-fc-ref-missing', 'target', 'rel', 'name']),
  img: new Set(['src', 'alt', 'width', 'height', 'loading']),
  td: new Set(['colspan', 'rowspan', 'align']),
  th: new Set(['colspan', 'rowspan', 'align', 'scope']),
  ol: new Set(['start']),
  code: new Set(['data-lang']),
  pre: new Set(['data-lang']),
  span: new Set([
    'data-fc-icon', 'data-fc-ref', 'data-fc-ref-missing',
    'data-fc-section', 'aria-label', 'role',
  ]),
  svg: new Set(['viewBox', 'xmlns', 'width', 'height', 'fill', 'aria-hidden']),
  path: new Set(['d']),
  sup: new Set(['data-fc-section']),
  sub: new Set(['data-fc-section']),
};
// `path` is only valid inside <svg>; allowed only when svg is.
ALLOWED_TAGS.add('path');

/** Tags whose CONTENT must also go, not just the tag. */
const VOID_CONTENT_TAGS = new Set(['script', 'style', 'iframe', 'object', 'embed', 'svg']);

/**
 * Drop a dangerous subtree outright. A stripped `<script>` that leaves its
 * body text behind turns code into visible prose; for these, the content goes
 * too. (An <svg> placeholder is in the allowlist and never reaches here.)
 */
function stripSubtree(html: string, tag: string): string {
  const open = new RegExp(`<${tag}\\b[^>]*>`, 'gi');
  const close = new RegExp(`</${tag}\\s*>`, 'gi');
  let out = html;
  // Repeat: nested <script> inside <script> is legal HTML and both must go.
  for (let i = 0; i < 5; i++) {
    const m = open.exec(out);
    if (!m) break;
    const end = close.exec(out.slice(m.index + m[0].length));
    const stop = end ? m.index + m[0].length + end.index + end[0].length : out.length;
    out = out.slice(0, m.index) + out.slice(stop);
    open.lastIndex = 0; close.lastIndex = 0;
  }
  // Unclosed remainder.
  out = out.replace(open, '').replace(close, '');
  return out;
}

/** Strip an event handler / anything with a colon-in-name we did not allow. */
function isSafeAttr(name: string, value: string, tag: string): boolean {
  const n = name.toLowerCase();
  if (n.startsWith('on')) return false;                 // every event handler
  if (n === 'style') return false;                      // CSS exfil / overlay
  if (n.startsWith('data-') || n.startsWith('aria-')) {
    return (TAG_ATTRS[tag]?.has(n) ?? false) || GLOBAL_ATTRS.has(n);
  }
  if (/(href|src)$/i.test(n)) {
    // markdown-it's validateLink already rejects these at the source; this is
    // the belt to its braces, and it also covers plugin-injected attributes.
    const v = value.trim().toLowerCase().replace(/[\u0000-\u0020]/g, '');
    if (v.startsWith('javascript:') || v.startsWith('vbscript:') || v.startsWith('data:')) return false;
  }
  return GLOBAL_ATTRS.has(n) || TAG_ATTRS[tag]?.has(n) === true;
}

/**
 * Allowlist pass over rendered markdown.
 *
 * Never throws. A malformed or adversarial input degrades to escaped text
 * rather than propagating. Idempotent: running it twice is the same as once.
 */
export function sanitizeMarkdownOutput(html: string): string {
  if (typeof html !== 'string' || html.length === 0) return '';

  let out = html;

  // 1. remove dangerous subtrees entirely (content included)
  for (const tag of VOID_CONTENT_TAGS) {
    if (tag === 'svg') continue; // svg is allowed; handled below as a tag
    out = stripSubtree(out, tag);
  }

  // 2. comments (can hide conditional-comment payloads)
  out = out.replace(/<!--[\s\S]*?-->/g, '');

  // 3. per-tag allowlist
  out = out.replace(/<\/?([a-zA-Z][a-zA-Z0-9-]*)((?:[^>"']|"[^"]*"|'[^']*')*)>/g,
    (match, rawTag: string, rawAttrs: string) => {
      const tag = rawTag.toLowerCase();
      const isClose = match[1] === '/';
      if (!ALLOWED_TAGS.has(tag)) return '';            // strip tag, keep text

      if (isClose) return `</${tag}>`;

      // rebuild the attribute list, dropping anything not explicitly allowed
      const kept: string[] = [];
      const attrRe = /([a-zA-Z_:][-a-zA-Z0-9_:.]*)\s*(?:=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'>]+)))?/g;
      let m: RegExpExecArray | null;
      while ((m = attrRe.exec(rawAttrs)) !== null) {
        const name = m[1];
        const value = m[2] ?? m[3] ?? m[4] ?? '';
        if (isSafeAttr(name, value, tag)) kept.push(`${name}="${escapeAttr(value)}"`);
      }
      return `<${tag}${kept.length ? ' ' + kept.join(' ') : ''}>`;
    });

  // 4. any stray < or > that survived outside a tag is inert text, but a
  //    lone "<" followed by a letter could re-open as a tag on a later pass.
  //    Escaping it keeps the pass idempotent.
  out = out.replace(/<(?![/a-zA-Z])/g, '&lt;');

  return out;
}

function escapeAttr(v: string): string {
  return v.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** The allowlist, exported so tests and consumers can assert against it. */
export const MARKDOWN_ALLOWED_TAGS: ReadonlySet<string> = ALLOWED_TAGS;
